import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "../components/section-header";
import { EmptyState } from "@/components/shared/empty-state";
import { ArrowRight } from "lucide-react";
import type { HomepageArticle } from "@/components/homepage/types";

interface CuratedSectionProps {
  title: string;
  icon: React.ElementType;
  items: HomepageArticle[];
  href: string;
  isLoading: boolean;
}

export function CuratedSection({ title, icon: Icon, items, href, isLoading }: CuratedSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby={`${title.toLowerCase().replace(/\s+/g, "-")}-heading`}>
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          icon={Icon}
          title={title}
          href={href}
        />
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item) => (
              <Link key={item.id} href={`/news/${item.id}`} className="group block">
                <Card className="hover:border-neutral-300 transition-all duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Badge variant="secondary" className="h-4 px-1.5 text-[0.625rem] font-medium rounded-full">{item.category ?? "News"}</Badge>
                    </div>
                    <h3 className="text-sm font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{item.author}</span>
                      <span className="text-neutral-300">·</span>
                      <span>{item.createdAt}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={Icon} description={`No ${title.toLowerCase()} content yet.`} />
        )}
      </div>
    </section>
  );
}